import { UpgradeModalContext, UserContext, VibratoClientContext } from "@/components/dashboard/contexts"
import { settings } from "@/settings"
import { getCallSvcs } from "@/lib/calls"
import {
  Book,
  Building,
  ChatBubble,
  Clock,
  DashboardDots,
  Download,
  NavArrowDown,
  Phone,
  Sparks,
  Star,
  Trophy
} from "iconoir-react"
import { useCallback, useContext, useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import CallList from "./CallList"
import Notifications from "./Notifications"
import SubscriptionSuccessModal from "./SubscriptionSuccessModal"
import { LoadingSpinner } from "../LoadingSpinner"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import CallTour from "./CallTour"
import DemoCallModal from "./DemoCallModal"

function daysAgo(days: number) {
  const datetime = new Date()
  datetime.setDate(datetime.getDate() - days)
  return datetime.toISOString()
}

function timeSavedSeconds(successCalls: any): number {
  return successCalls.reduce((acc: any, call: any) => {
    acc += call.duration || 0
    return acc
  }, 0)
}

const timePeriods: Record<string, string> = {
  all: "All time",
  month: "This month",
  week: "This week"
}

function TimePeriodButton({ currentTp, setCurrentTp }: { currentTp: any; setCurrentTp: any }) {
  const nonSelected = Object.keys(timePeriods)
  const changeTp = useCallback((tp: string) => {
    setCurrentTp(tp)
  }, [])

  return (
    <Menu as="div" className="relative inline-flex">
      <MenuButton className="sm:w-[176px] h-[40px] p-[10px] inline-flex justify-center items-center gap-2 rounded-md border font-medium border-slate-200 shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-600 transition-all text-sm dark:bg-slate-900 dark:hover:bg-slate-800 dark:border-gray-700 dark:text-gray-400 dark:hover:text-white dark:focus:ring-offset-gray-800">
        <div className="grow flex justify-between text-gray-700 text-base font-normal">
          {timePeriods[currentTp]}
          <NavArrowDown color="#4B95FF" />
        </div>
      </MenuButton>

      <MenuItems anchor="bottom" className="z-60 mt-2 min-w-60 w-72 bg-white shadow-md rounded-lg p-2">
        {nonSelected.map((tp) => (
          <MenuItem key={tp}>
            <a
              onClick={() => changeTp(tp)}
              href="#"
              className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-gray-800 hover:bg-gray-100 data-focus:bg-gray-100 data-focus:ring-2 data-focus:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            >
              {timePeriods[tp]}
            </a>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  )
}

function QuickActionCard({ icon, title, description, onClick, gradient }: any) {
  return (
    <div
      className={`min-w-[200px] lg:max-w-[250px] h-[150px] sm:h-[200px] p-4 sm:p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform ${gradient} flex flex-col`}
      onClick={onClick}
    >
      <div className="flex items-start sm:items-center gap-3 mb-2 h-fit">
        <div className="p-2 sm:p-3 bg-white/20 rounded-xl shrink-0">{icon}</div>
        <h3 className="text-base sm:text-lg font-semibold text-white">{title}</h3>
      </div>
      <p className="text-white/80 text-xs sm:text-sm ml-1 h-fit">{description}</p>
    </div>
  )
}

function StatsCard({ icon, title, value, trend = null }: any) {
  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-md hover:shadow-lg transition-all duration-200">
      <div className="flex items-start gap-3 h-full">
        <div className="p-2 bg-purple-100 rounded-lg shrink-0">{icon}</div>
        <div className="flex flex-col h-full">
          <h3 className="min-h-4 text-sm sm:text-base text-gray-600 font-medium mb-0.5 sm:mb-2">{title}</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-lg sm:text-2xl font-bold text-gray-900">{value}</span>
            {trend && (
              <span className={`text-xs sm:text-sm ${trend >= 0 ? "text-green-500" : "text-red-500"}`}>
                {trend > 0 ? "+" : ""}
                {trend}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function EmptyStateSection() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-white rounded-2xl shadow-md">
      <div className="p-4 bg-purple-100 rounded-xl mb-6">
        <Sparks width="32" height="32" className="text-purple-600" />
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-3">Welcome to Vibrato!</h2>
      <p className="text-gray-600 text-center max-w-lg mb-8">
        Get started by making your first automated call. Our AI will handle the conversation while you focus on what
        matters.
      </p>
      <Link to="/new-call">
        <button className="px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
          <Phone className="w-5 h-5" />
          Make Your First Call
        </button>
      </Link>
    </div>
  )
}

export default function DashboardPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const vibratoClient = useContext(VibratoClientContext)
  const user = useContext(UserContext)
  const [since, setSince] = useState<string>(daysAgo(365))
  const upgradeModal = useContext(UpgradeModalContext)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const [showDemoCallModal, setShowDemoCallModal] = useState(false)

  const [calls, setCalls] = useState<any | null>(null)
  const [callSvcsByStatus, setCallSvcsByStatus] = useState<any>({})
  const [callsLoading, setCallsLoading] = useState(true)
  const [currentTp, setCurrentTp] = useState<string>("all")

  const showSubSuccessModal = searchParams.get("subscription_success") == "true"

  useEffect(() => {
    document.title = "Vibrato - Dashboard"
  }, [])

  // Redirect to welcome tour if necessary
  useEffect(() => {
    if (user) {
      const tourCompletedKey = `vibrato_intro_tour_completed_${user.id}`
      const tourCompleted = localStorage.getItem(tourCompletedKey)

      const urlParams = new URLSearchParams(window.location.search)
      const forceTour = urlParams.get("intro_flow") === "1"

      // Don't redirect to welcome if user just completed subscription
      if (forceTour || (user.show_intro_flow && tourCompleted !== "true")) {
        setShowWelcome(true)
      }
    }
  }, [user, navigate, setShowWelcome])

  useEffect(() => {
    let isMounted = true
    vibratoClient
      .getPlan()
      .then((fetchedPlan: any) => {
        if (isMounted) {
          if (fetchedPlan?.minutes_remaining != null && fetchedPlan.minutes_remaining <= 0) {
            upgradeModal.setShowModal(true)
            upgradeModal.setError("You have run out of call minutes. Upgrade now to continue making calls.")
          }
        }
      })
      .catch((error: any) => {
        console.error("Error fetching user plan:", error)
      })
      .finally(() => {
        if (isMounted) {
          setInitialLoadComplete(true)
        }
      })
    return () => {
      isMounted = false
    }
  }, [vibratoClient])

  const fetchCalls = useCallback(async () => {
    if (vibratoClient == null || !initialLoadComplete) {
      return
    }
    setCallsLoading(true)
    setCalls(null)
    setCallSvcsByStatus({})

    try {
      const fetchedCalls = await vibratoClient.getCalls({
        status: "active",
        since: since,
        pageSize: 1000
      })
      const svcs = getCallSvcs(fetchedCalls.results)
      setCalls(fetchedCalls.results)
      setCallSvcsByStatus(svcs)
    } catch (error) {
      console.error("Error fetching calls:", error)
      setCalls([])
      setCallSvcsByStatus({})
    } finally {
      setCallsLoading(false)
    }
  }, [vibratoClient, since, initialLoadComplete])

  useEffect(() => {
    fetchCalls()
  }, [fetchCalls])

  useEffect(() => {
    if (currentTp === "all") {
      setSince(daysAgo(365))
    } else if (currentTp === "week") {
      setSince(daysAgo(7))
    } else if (currentTp === "month") {
      setSince(daysAgo(30))
    }
  }, [currentTp])

  // if (!initialLoadComplete) {
  //   return <Loading />
  // }

  const totalCalls = calls && !callsLoading ? callSvcsByStatus.all?.length || 0 : 0
  const timeSavedMinutes = calls && !callsLoading ? Math.round(timeSavedSeconds(calls) / 60) : 0
  const showEmptyState = !callsLoading && calls !== null && totalCalls === 0

  if (showWelcome) {
    return (
      <>
        {showSubSuccessModal && <SubscriptionSuccessModal />}
        <CallTour />
      </>
    )
  }

  return (
    <>
      {showSubSuccessModal && <SubscriptionSuccessModal />}
      <DemoCallModal showModal={showDemoCallModal} setShowModal={setShowDemoCallModal} />
      <div id="dashboard" className="min-h-full h-fit w-full p-6 md:p-8 pb-4">
        <div className="flex flex-col gap-8 min-h-fit h-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
            <div className="flex items-center gap-4 mb-4 sm:mb-0 w-full sm:w-fit justify-center sm:justify-start">
              <div className="p-3 bg-purple-100 rounded-xl">
                <DashboardDots width="27" className="text-purple-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600">Track your automation progress</p>
              </div>
            </div>

            <div className="w-full flex flex-row flex-wrap gap-4 sm:w-auto justify-center items-center sm:ml-4">
              <div className="min-w-fit">
                <TimePeriodButton currentTp={currentTp} setCurrentTp={setCurrentTp} />
              </div>

              <Link to="/new-call" className="">
                <button className="min-w-[180px] px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />
                  Place a Call
                </button>
              </Link>
            </div>
          </div>

          {(user.plan == "beta" || user.plan == "beta-plus") && (
            <div className="max-w-4xl mx-auto w-full">
              <Notifications
                notifs={[
                  {
                    type: "banner",
                    header: "🚀 Beta Update",
                    id: "beta-msg",
                    message:
                      "The Vibrato Beta is finishing at the end of April 2024. Upgrade to Plus for unlimited calling, voice cloning, and priority support!"
                  }
                ]}
              />
            </div>
          )}

          <div className="h-fit flex gap-4 pb-4 -mx-4 px-4 overflow-x-auto">
            {user.plan === "freemium" && (
              <QuickActionCard
                icon={<Phone className="w-6 h-6 text-white" />}
                title="Talk to Our AI"
                description="Experience the magic firsthand. Call our AI for a mind-bending conversation."
                gradient="bg-gradient-to-br from-purple-500 to-indigo-600"
                onClick={() => {
                  setShowDemoCallModal(true)
                }}
              />
            )}
            <QuickActionCard
              icon={<Book className="w-6 h-6 text-white" />}
              title="Explore the Task Library"
              description="Save time with our library of ready-to-go automated calls."
              gradient="bg-gradient-to-br from-blue-500 to-cyan-500"
              onClick={() => {
                navigate("/call-library")
              }}
            />
            {!settings.SHOW_NATIVE_APP && (
              <QuickActionCard
                icon={<Download className="w-6 h-6 text-white" />}
                title="Get our Mobile App"
                description="Make and manage calls on the go. Download the Vibrato app for iOS and Android."
                gradient="bg-gradient-to-br from-green-400 to-cyan-400"
                onClick={() => {
                  navigate("/mobile-app")
                }}
              />
            )}
            <QuickActionCard
              icon={<ChatBubble className="w-6 h-6 text-white" />}
              title="Chat with Your AI Assistant"
              description="Just describe the call you want, and our AI assistant will handle it."
              gradient="bg-gradient-to-br from-teal-500 to-green-500"
              onClick={() => {
                navigate("/chat")
              }}
            />
            <QuickActionCard
              icon={<Sparks className="w-6 h-6 text-white" />}
              title="Clone Your Voice"
              description="Create your custom AI voice for more personal interactions"
              gradient="bg-gradient-to-br from-emerald-500 to-lime-500"
              onClick={() => {
                navigate("/settings/voice?clone=true")
              }}
            />
            <QuickActionCard
              icon={<Star className="w-6 h-6 text-white" />}
              title="Upgrade Plan"
              description="Get unlimited minutes and premium features"
              gradient="bg-gradient-to-br from-amber-500 to-orange-500"
              onClick={() => {
                upgradeModal.setShowModal(true)
              }}
            />
            <QuickActionCard
              icon={<Phone className="w-6 h-6 text-white" />}
              title="Add Phone Number"
              description="Connect a new phone number to make and receive calls"
              gradient="bg-gradient-to-br from-orange-500 to-red-500"
              onClick={() => {
                navigate("/phone-numbers?add=true")
              }}
            />
            <QuickActionCard
              icon={<Building className="w-6 h-6 text-white" />}
              title="Utility Directory"
              description="Browse common local business providers and execute common tasks"
              gradient="bg-gradient-to-br from-pink-500 to-rose-500"
              onClick={() => {
                window.open("https://www.getvibrato.com/c/biz", "_blank")
              }}
            />
          </div>
          {callsLoading ? (
            <div className="flex flex-col items-center justify-center mb-4 py-12 px-4 sm:px-6 lg:px-8 bg-white rounded-2xl shadow-md w-full h-fit">
              <LoadingSpinner className="w-8 h-8 text-purple-600 mb-4" />
              <p className="text-gray-600">Loading calls...</p>
            </div>
          ) : showEmptyState ? (
            <EmptyStateSection />
          ) : (
            <>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-6">
                <StatsCard
                  icon={<Clock className="w-5 h-5 text-purple-600" />}
                  title="Time Saved"
                  value={`${timeSavedMinutes.toLocaleString()} mins`}
                />
                <StatsCard
                  icon={<Phone className="w-5 h-5 text-purple-600" />}
                  title="Total Calls"
                  value={totalCalls}
                />
                <StatsCard
                  icon={<Trophy className="w-5 h-5 text-purple-600" />}
                  title="Scheduled Calls"
                  value={callSvcsByStatus.scheduled?.length || 0}
                />
                <StatsCard
                  icon={<Star className="w-5 h-5 text-purple-600" />}
                  title="Incomplete Calls"
                  value={callSvcsByStatus.missing_data?.length || 0}
                />
              </div>
              <div className="">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h2>
                <CallList callSvcsByStatus={callSvcsByStatus} />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
